import { Monaco } from '@monaco-editor/react';

/**
 * Extract all top-level parameter names from the editor model text.
 * Matches lines of the form: `  varName = ...;`
 */
function extractParamNamesFromModel(model: any): string[] {
  const text: string = model.getValue();
  const names: string[] = [];
  const seen = new Set<string>();
  // Same regex the parser uses (simplified, no capturing groups for value)
  const re = /^[ \t]*(\$?[a-zA-Z_][a-zA-Z0-9_]*)\s*=/gm;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const name = m[1];
    if (!seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  }
  return names;
}

/**
 * Detect whether the cursor is inside a visibility `{...}` expression within
 * a line comment or a group block comment.
 *
 * Matches:
 *   `// ...{<cursor>`          (parameter inline comment)
 *   `/* [Group Name] {<cursor>`  (group block comment)
 */
function isInsideVisibilityExpr(lineText: string, column: number): boolean {
  const before = lineText.slice(0, column - 1);
  // Inside a line comment that has an open { not yet closed
  if (/\/\/.*\{[^}]*$/.test(before)) return true;
  // Inside a block comment group header that has an open { not yet closed
  if (/\/\*\s*\[[^\]]*\]\s*\{[^}]*$/.test(before)) return true;
  return false;
}

const VISIBILITY_OPERATORS = [
  '==',
  '!=',
  '<=',
  '>=',
  '<',
  '>',
  '&&',
  '||',
  '!',
];
const VISIBILITY_KEYWORDS = ['true', 'false'];

export const getOpenSCADCompletionProvider = (monaco: Monaco) => {
  return {
    triggerCharacters: ['{', ' ', '&', '|', '!', '='],

    provideCompletionItems: (model: any, position: any) => {
      const word = model.getWordUntilPosition(position);
      const lineText: string = model.getLineContent(position.lineNumber);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      // ------------------------------------------------------------------
      // Context-aware completions inside {visibility expression} blocks
      // ------------------------------------------------------------------
      if (isInsideVisibilityExpr(lineText, position.column)) {
        const paramNames = extractParamNamesFromModel(model);
        const suggestions: any[] = [];

        // Parameter name identifiers
        for (const name of paramNames) {
          suggestions.push({
            label: name,
            kind: monaco.languages.CompletionItemKind.Variable,
            insertText: name,
            documentation: `Parameter: ${name}`,
            detail: 'parameter',
            sortText: '0_' + name,
            range,
          });
        }

        // Boolean keywords
        for (const kw of VISIBILITY_KEYWORDS) {
          suggestions.push({
            label: kw,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: kw,
            sortText: '1_' + kw,
            range,
          });
        }

        // Operator snippets — only when not already mid-word
        if (!word.word) {
          for (const op of VISIBILITY_OPERATORS) {
            suggestions.push({
              label: op,
              kind: monaco.languages.CompletionItemKind.Operator,
              insertText: op + ' ',
              sortText: '2_' + op,
              range,
            });
          }
        }

        return { suggestions };
      }

      const suggestions = [
        // Standard OpenSCAD 3D Primitives
        {
          label: 'cube',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'cube([${1:10}, ${2:10}, ${3:10}], center=${4:true});',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Creates a cube.',
          range: range,
        },
        {
          label: 'sphere',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'sphere(r=${1:5}, $fn=${2:30});',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Creates a sphere.',
          range: range,
        },
        {
          label: 'cylinder',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText:
            'cylinder(h=${1:10}, r=${2:5}, center=${3:true}, $fn=${4:30});',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Creates a cylinder.',
          range: range,
        },
        {
          label: 'polyhedron',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'polyhedron(points=${1:[]}, faces=${2:[]});',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Creates a polyhedron.',
          range: range,
        },

        // Standard OpenSCAD 2D Primitives
        {
          label: 'square',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'square([${1:10}, ${2:10}], center=${3:true});',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Creates a square.',
          range: range,
        },
        {
          label: 'circle',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'circle(r=${1:5}, $fn=${2:30});',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Creates a circle.',
          range: range,
        },
        {
          label: 'polygon',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'polygon(points=${1:[]});',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Creates a polygon.',
          range: range,
        },
        {
          label: 'text',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText:
            'text("${1:text}", size=${2:10}, font="${3:Liberation Sans}");',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Creates text.',
          range: range,
        },

        // Transformations
        {
          label: 'translate',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'translate([${1:0}, ${2:0}, ${3:0}])',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Translates geometry.',
          range: range,
        },
        {
          label: 'rotate',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'rotate([${1:0}, ${2:0}, ${3:0}])',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Rotates geometry.',
          range: range,
        },
        {
          label: 'scale',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'scale([${1:1}, ${2:1}, ${3:1}])',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Scales geometry.',
          range: range,
        },
        {
          label: 'mirror',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'mirror([${1:0}, ${2:0}, ${3:1}])',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Mirrors geometry.',
          range: range,
        },
        {
          label: 'multmatrix',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'multmatrix(${1:m})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Multiplies the geometry by a matrix.',
          range: range,
        },
        {
          label: 'color',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'color("${1:red}")',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Colors geometry.',
          range: range,
        },

        // Material system
        {
          label: 'material_def',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText:
            'material_def(metalness=${1:0.0}, roughness=${2:0.5}, emissive=[${3:0},${4:0},${5:0}], emissive_intensity=${6:0.0})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation:
            'Defines PBR material properties. Use -1 for any property to inherit the global default. Properties: metalness (0-1), roughness (0-1), emissive ([r,g,b] 0-1), emissive_intensity (0+).',
          range: range,
        },
        {
          label: 'material',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'material(${1:material_name})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation:
            'Applies a material definition to child geometry. Must wrap color(): material(mat) color([r,g,b]) geometry();',
          range: range,
        },
        {
          label: 'offset',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'offset(r=${1:1})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Offsets 2D geometry.',
          range: range,
        },
        {
          label: 'hull',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'hull() {\n\t${1}\n}',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Creates a convex hull.',
          range: range,
        },
        {
          label: 'minkowski',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'minkowski() {\n\t${1}\n}',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Minkowski sum.',
          range: range,
        },

        // Boolean Operations
        {
          label: 'union',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'union() {\n\t${1}\n}',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Union of geometry.',
          range: range,
        },
        {
          label: 'difference',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'difference() {\n\t${1}\n}',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Difference of geometry.',
          range: range,
        },
        {
          label: 'intersection',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'intersection() {\n\t${1}\n}',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Intersection of geometry.',
          range: range,
        },

        // Extrusions
        {
          label: 'linear_extrude',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText:
            'linear_extrude(height=${1:10}, center=${2:true}, convexity=${3:10}, twist=${4:0})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Linear extrusion.',
          range: range,
        },
        {
          label: 'rotate_extrude',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText:
            'rotate_extrude(angle=${1:360}, convexity=${2:2}, $fn=${3:30})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Rotational extrusion.',
          range: range,
        },

        // Keywords
        {
          label: 'module',
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: 'module ${1:name}(${2}) {\n\t${3}\n}',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range: range,
        },
        {
          label: 'function',
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: 'function ${1:name}(${2}) = ${3};',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range: range,
        },
        {
          label: 'include',
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: 'include <${1}>;',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range: range,
        },
        {
          label: 'use',
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: 'use <${1}>;',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range: range,
        },
        {
          label: 'echo',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'echo(${1});',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range: range,
        },

        // Special variables
        {
          label: '$fn',
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: '$fn = ${1:30};',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range: range,
        },
        {
          label: '$fa',
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: '$fa = ${1:12};',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range: range,
        },
        {
          label: '$fs',
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: '$fs = ${1:2};',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range: range,
        },
        {
          label: '$t',
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: '$t',
          range: range,
        },
        {
          label: '$vpr',
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: '$vpr',
          range: range,
        },
        {
          label: '$vpt',
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: '$vpt',
          range: range,
        },
        {
          label: '$vpd',
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: '$vpd',
          range: range,
        },

        // BOSL2 Common Features
        {
          label: 'BOSL2 std',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: 'include <BOSL2/std.scad>',
          documentation: 'Includes the standard BOSL2 library.',
          range: range,
        },
        {
          label: 'cuboid',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText:
            'cuboid([${1:10}, ${2:10}, ${3:10}], rounding=${4:0}, chamfer=${5:0}, anchor=CENTER);',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation:
            'BOSL2: A replacement for cube() that offers rounding, chamfering, and alignment.',
          range: range,
        },
        {
          label: 'cyl',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText:
            'cyl(h=${1:10}, d=${2:10}, rounding=${3:0}, chamfer=${4:0}, anchor=CENTER);',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation:
            'BOSL2: A replacement for cylinder() that offers rounding, chamfering, and alignment.',
          range: range,
        },
        {
          label: 'prismoid',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText:
            'prismoid(size1=[${1:10},${2:10}], size2=[${3:5},${4:5}], h=${5:10}, shift=[${6:0},${7:0}], rounding=${8:0}, anchor=CENTER);',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation:
            'BOSL2: Creates a prism with different rectangular top and bottom.',
          range: range,
        },
        {
          label: 'rect_tube',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText:
            'rect_tube(h=${1:10}, size=[${2:10}, ${3:10}], wall=${4:1}, rounding=${5:0}, anchor=CENTER);',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Creates a rectangular tube.',
          range: range,
        },
        {
          label: 'tube',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'tube(h=${1:10}, od=${2:10}, id=${3:8}, anchor=CENTER);',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Creates a cylindrical tube.',
          range: range,
        },
        {
          label: 'spheroid',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'spheroid(d=${1:10}, style="${2:aligned}");',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: A replacement for sphere().',
          range: range,
        },
        {
          label: 'recolor',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'recolor("${1:red}")',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Sets the color of children.',
          range: range,
        },
        {
          label: 'attach',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'attach(${1:TOP})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Attaches children to a parent.',
          range: range,
        },
        {
          label: 'diff',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'diff("${1:remove}")',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Difference operator using tags.',
          range: range,
        },
        {
          label: 'tag',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'tag("${1:name}")',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Tags geometry for diff/intersect/etc.',
          range: range,
        },
        {
          label: 'force_tag',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'force_tag("${1:name}")',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Forces a tag on geometry.',
          range: range,
        },
        {
          label: 'grid_copies',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText:
            'grid_copies(spacing=[${1:10}, ${2:10}], n=[${3:3}, ${4:3}])',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Distributes copies on a grid.',
          range: range,
        },
        {
          label: 'xcopies',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'xcopies(spacing=${1:10}, n=${2:3})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Distributes copies along X axis.',
          range: range,
        },
        {
          label: 'ycopies',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'ycopies(spacing=${1:10}, n=${2:3})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Distributes copies along Y axis.',
          range: range,
        },
        {
          label: 'zcopies',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'zcopies(spacing=${1:10}, n=${2:3})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Distributes copies along Z axis.',
          range: range,
        },
        {
          label: 'fwd',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'fwd(${1:10})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Moves forward (Y+).',
          range: range,
        },
        {
          label: 'back',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'back(${1:10})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Moves back (Y-).',
          range: range,
        },
        {
          label: 'left',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'left(${1:10})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Moves left (X-).',
          range: range,
        },
        {
          label: 'right',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'right(${1:10})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Moves right (X+).',
          range: range,
        },
        {
          label: 'up',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'up(${1:10})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Moves up (Z+).',
          range: range,
        },
        {
          label: 'down',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'down(${1:10})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Moves down (Z-).',
          range: range,
        },
        {
          label: 'xrot',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'xrot(${1:90})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Rotates around X axis.',
          range: range,
        },
        {
          label: 'yrot',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'yrot(${1:90})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Rotates around Y axis.',
          range: range,
        },
        {
          label: 'zrot',
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: 'zrot(${1:90})',
          insertTextRules:
            monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'BOSL2: Rotates around Z axis.',
          range: range,
        },
      ];

      return { suggestions: suggestions };
    },
  };
};
